import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { commApi } from './CommunityContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Anchor, ArrowLeft, MapPin, Tag, Loader2, Sparkles, Ship, Clock } from 'lucide-react';

const HERO = 'https://images.unsplash.com/photo-1559599746-8823b38544c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const daysLeft = (d) => {
  if (!d) return null;
  const exp = new Date(d);
  exp.setHours(23, 59, 59, 999);
  const today = new Date();
  return Math.ceil((exp - today) / 86400000);
};

const CountdownBadge = ({ date }) => {
  const days = daysLeft(date);
  if (days === null || days < 0) return null;
  const label = days === 0 ? 'Scade oggi!' : days === 1 ? 'Ultimo giorno!' : `Ancora ${days} giorni!`;
  const urgent = days <= 7;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${urgent ? 'bg-rose-500 text-white animate-pulse' : 'bg-slate-800/80 text-white'}`}
      data-testid="promo-countdown"
    >
      <Clock className="w-3.5 h-3.5" /> {label}
    </span>
  );
};

const waLink = (p, number) => {
  const digits = (number || '').replace(/\D/g, '');
  const text =
    `Ciao! Sono interessato/a a questa promozione:\n\n*${p.title}*` +
    (p.destination ? `\n📍 ${p.destination}` : '') +
    (p.price ? `\n💶 ${p.price}` : '') +
    (p.description ? `\n\n${p.description}` : '') +
    (p.image_url ? `\n\n${p.image_url}` : '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const CommunityPromotions = () => {
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [whatsapp, setWhatsapp] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    commApi
      .get('/community/promotions')
      .then((r) => {
        setPromos(r.data.promotions);
        setWhatsapp(r.data.whatsapp);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur border-b border-cyan-900/40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white font-bold text-lg cursor-pointer" onClick={() => navigate('/community')}>
            <Anchor className="w-6 h-6 text-cyan-400" />
            Tribù in Viaggio Community
          </div>
          <Button variant="ghost" className="text-cyan-100 hover:text-white hover:bg-white/10" onClick={() => navigate('/community')} data-testid="promo-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Community
          </Button>
        </div>
      </nav>

      <div className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-cyan-700/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-200 text-sm font-medium mb-4 border border-cyan-400/30">
            <Sparkles className="w-4 h-4" /> Offerte selezionate per te
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white">Promozioni Crociere</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-xl">
            Scopri le nostre offerte del momento. Ti interessa? Contattaci su WhatsApp con un tap: avrai subito il quadro completo e potrai farci tutte le domande.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : promos.length === 0 ? (
          <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed" data-testid="promo-empty">
            <Ship className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Nessuna promozione attiva al momento. Torna presto!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promos.map((p) => (
              <Card key={p.id} className="overflow-hidden group hover:shadow-xl transition-shadow" data-testid={`promo-card-${p.id}`}>
                {p.image_url && (
                  <div className="h-48 overflow-hidden relative">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {p.expires_at && <div className="absolute top-2 right-2"><CountdownBadge date={p.expires_at} /></div>}
                  </div>
                )}
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-800">{p.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 items-center">
                    {!p.image_url && p.expires_at && <CountdownBadge date={p.expires_at} />}
                    {p.destination && (
                      <span className="inline-flex items-center gap-1 text-xs bg-cyan-50 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full">
                        <MapPin className="w-3 h-3" /> {p.destination}
                      </span>
                    )}
                    {p.price && (
                      <span className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-semibold">
                        <Tag className="w-3 h-3" /> {p.price}
                      </span>
                    )}
                  </div>
                  {p.description && <p className="text-sm text-slate-500 mt-3 line-clamp-3">{p.description}</p>}
                  <a href={waLink(p, whatsapp)} target="_blank" rel="noopener noreferrer" data-testid={`promo-whatsapp-${p.id}`}>
                    <Button className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 text-white">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current"><path d="M17.5 14.4c-.3-.1-1.7-.9-2-1s-.5-.1-.7.2-.8 1-.9 1.1-.3.2-.6 0a8 8 0 0 1-2.4-1.5 9 9 0 0 1-1.6-2c-.2-.3 0-.5.1-.6l.5-.5.3-.5v-.5l-1-2.2c-.2-.6-.4-.5-.6-.5h-.6a1 1 0 0 0-.8.4A3.4 3.4 0 0 0 5.9 9a5.9 5.9 0 0 0 1.2 3.1 13.4 13.4 0 0 0 5.2 4.6c.7.3 1.3.5 1.7.6a4 4 0 0 0 1.9.1c.6-.1 1.7-.7 2-1.4s.3-1.2.2-1.4-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.3A10 10 0 1 0 12 2z"/></svg>
                      Contattaci su WhatsApp
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPromotions;
